import { Platform } from 'react-native';
import { doc, getDoc, getDocFromServer, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getInstalledAppVersion } from '../utils/appVersion';
import { evaluateInstalledVersion, type NudgeState, type SurveyConfig, type VersionCheck } from '../utils/optionalNudges';
import type { SurveyResponsePayload } from '../utils/feedbackSurvey';

// Firestore I/O for optional nudges (firestore.rules: nudgeState/{uid} is the
// owner's own doc — admins and Cloud Functions read it for push cadence;
// feedbackResponses are create-once by the responder, admin-read only;
// appConfig/feedbackSurvey is public-read, admin-write).

const STATE = 'nudgeState';

// Emulator web builds only (see firebase.ts): lets local tooling preview the
// native survey steps in a browser —
//   globalThis.__tonyteNudgeDebug = { native: true, versionCheck: { status: 'outdated', … } }
declare const __TONYTE_USE_EMULATORS__: boolean | undefined;
const nudgeDebug = (): { native?: boolean; versionCheck?: VersionCheck } | null => (
  typeof __TONYTE_USE_EMULATORS__ !== 'undefined' && __TONYTE_USE_EMULATORS__ ? (globalThis as any).__tonyteNudgeDebug || null : null
);

export function subscribeNudgeState(uid: string, onState: (state: NudgeState | null) => void): () => void {
  return onSnapshot(
    doc(firestore, STATE, uid),
    snap => onState(snap.exists() ? (snap.data() as NudgeState) : {}),
    () => onState(null),
  );
}

/** Deep-merges `patch` into the user's nudge state (maps merge, values replace). */
export async function patchNudgeState(uid: string, patch: Partial<NudgeState>): Promise<void> {
  await setDoc(doc(firestore, STATE, uid), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}

export async function loadSurveyConfig(): Promise<SurveyConfig> {
  try {
    const snap = await getDoc(doc(firestore, 'appConfig', 'feedbackSurvey'));
    return snap.exists() ? (snap.data() as SurveyConfig) : null;
  } catch {
    return null;
  }
}

/**
 * Installed build vs the *server's* appConfig/update. A cached or missing
 * config, or missing version info, is 'unknown' — never "current".
 */
export async function checkInstalledVersion(): Promise<VersionCheck & { versionName?: string; versionCode?: number }> {
  const debug = nudgeDebug();
  if (debug?.versionCheck) return debug.versionCheck;
  try {
    const [installed, snap] = await Promise.all([
      getInstalledAppVersion().catch(() => null),
      getDocFromServer(doc(firestore, 'appConfig', 'update')),
    ]);
    const config = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
    return { ...evaluateInstalledVersion(config, installed), versionName: installed?.versionName, versionCode: installed?.versionCode };
  } catch {
    return { status: 'unknown' };
  }
}

/** One response per user and survey (doc id `${surveyId}_${uid}`; rules forbid updates). */
export async function submitSurveyResponse(uid: string, payload: SurveyResponsePayload): Promise<void> {
  await setDoc(doc(firestore, 'feedbackResponses', `${payload.surveyId}_${uid}`), {
    ...payload,
    uid,
    submittedAt: serverTimestamp(),
  });
  await patchNudgeState(uid, { surveys: { [payload.surveyId]: { submittedAt: Date.now() } } });
}

export const isNativePlatform = () => nudgeDebug()?.native === true || Platform.OS === 'ios' || Platform.OS === 'android';
